import { Vector4 } from '@zephyr3d/base';
import { RenderQueue } from '../render_queue.js';
import { LightPass } from '../lightpass.js';
import { ShadowMapPass } from '../shadowmap_pass.js';
import { DepthPass } from '../depthpass.js';
import { ClusteredLight } from '../cluster_light.js';
import { buildHiZ } from '../hzb.js';
import '../primitive.js';
import '@zephyr3d/device';
import { CopyBlitter } from '../../blitter/copy.js';
import { fetchSampler } from '../../utility/misc.js';
import { MaterialVaryingFlags } from '../../values.js';
import { AbstractPostEffect, PostEffectLayer } from '../../posteffect/posteffect.js';
import { RenderGraph } from './rendergraph.js';
import { RenderGraphExecutor } from './executor.js';
import { DevicePoolAllocator } from './device_pool_allocator.js';
import { HistoryResourceManager } from './history_resource_manager.js';
import { RGHistoryResources } from './history_resources.js';
import { renderObjectColors } from '../gpu_picking.js';
import { BoxShape } from '../../shapes/box.js';
import '../../shapes/capsule.js';
import '../../shapes/cylinder.js';
import '../../shapes/torus.js';
import '../../shapes/plane.js';
import '../../shapes/sphere.js';
import '../../shapes/tetrahedron.js';

// ─── Shared Pass Instances ──────────────────────────────────────────
const _scenePass = new LightPass();
const _depthPass = new DepthPass();
const _shadowMapPass = new ShadowMapPass();
const _clusters = [];
const _devicePoolAllocator = new DevicePoolAllocator();
let _backDepthColorState = null;
let _frontDepthColorState = null;
const SURFACE_MRT_FLAGS = MaterialVaryingFlags.SSR_STORE_ROUGHNESS | MaterialVaryingFlags.SSS_STORE_PROFILE | MaterialVaryingFlags.SSS_STORE_DIFFUSE | MaterialVaryingFlags.SSS_STORE_NORMAL | MaterialVaryingFlags.SSS_STORE_TRANSMISSION;
function getClusteredLight() {
    return _clusters.length > 0 ? _clusters.pop() : new ClusteredLight();
}
function freeClusteredLight(cl) {
    _clusters.push(cl);
}
function getCoreMaterial(material) {
    return material?.coreMaterial ?? material ?? null;
}
function hasSSSMaterialCore(material) {
    return !!getCoreMaterial(material)?.subsurfaceProfile;
}
function renderQueueHasActiveSSS(renderQueue) {
    const itemList = renderQueue.itemList;
    if (!itemList) {
        return false;
    }
    const lists = [
        ...itemList.opaque.lit,
        ...itemList.opaque.unlit
    ];
    for (const list of lists){
        for (const material of list.materialList){
            if (hasSSSMaterialCore(material)) {
                return true;
            }
        }
    }
    return false;
}
function filterActualSSSItemList(items) {
    return items.filter((item)=>hasSSSMaterialCore(item.drawable.getMaterial?.()));
}
function filterActualSSSMaterialList(materialList) {
    const filtered = new Set();
    materialList.forEach((mat)=>{
        if (hasSSSMaterialCore(mat)) {
            filtered.add(mat);
        }
    });
    return filtered;
}
function cloneActualSSSListInfo(source, _targetQueue) {
    return {
        itemList: filterActualSSSItemList(source.itemList),
        skinItemList: filterActualSSSItemList(source.skinItemList),
        morphItemList: filterActualSSSItemList(source.morphItemList),
        skinAndMorphItemList: filterActualSSSItemList(source.skinAndMorphItemList),
        instanceItemList: filterActualSSSItemList(source.instanceItemList),
        materialList: filterActualSSSMaterialList(source.materialList),
        instanceList: {},
        renderQueue: source.renderQueue
    };
}
function cloneActualSSSBundle(source, targetQueue) {
    return {
        lit: source.lit.map((info)=>cloneActualSSSListInfo(info)),
        unlit: source.unlit.map((info)=>cloneActualSSSListInfo(info))
    };
}
function hasAnyActualSSSItems(renderItems) {
    return renderItems.some((info)=>info.itemList.length > 0 || info.skinItemList.length > 0 || info.morphItemList.length > 0 || info.skinAndMorphItemList.length > 0 || info.instanceItemList.length > 0);
}
function createActualSSSRenderQueue(renderQueue) {
    const itemList = renderQueue.itemList;
    if (!itemList) {
        return null;
    }
    const queue = new RenderQueue(_scenePass);
    const sssOpaque = cloneActualSSSBundle(itemList.opaque);
    if (!hasAnyActualSSSItems([
        ...sssOpaque.lit,
        ...sssOpaque.unlit
    ])) {
        queue.dispose();
        return null;
    }
    const emptyBundle = {
        lit: [],
        unlit: []
    };
    const target = queue;
    target._itemList = {
        opaque: sssOpaque,
        transmission: emptyBundle,
        transparent: emptyBundle,
        transmission_trans: emptyBundle
    };
    target._shadowedLightList = renderQueue.shadowedLights;
    target._unshadowedLightList = renderQueue.unshadowedLights;
    target._sunLight = renderQueue.sunLight;
    target._primaryDirectionalLight = renderQueue.primaryDirectionalLight;
    target._primaryTransmissionLight = renderQueue.primaryTransmissionLight;
    target._needSceneColor = false;
    target._needSceneDepth = false;
    target._needSceneColorWithDepth = false;
    target._drawTransparent = false;
    return queue;
}
function getSurfaceTextureFormat(ctx) {
    const caps = ctx.device.getDeviceCaps?.();
    return caps?.textureCaps.supportHalfFloatColorBuffer ? 'rgba16f' : 'rgba8unorm';
}
function getTextureFormatBytes(ctx, format) {
    return ctx.device.getDeviceCaps().textureCaps.getTextureFormatInfo(format).size;
}
function shouldStoreSSSDiffuse(ctx) {
    return ctx.camera.sssStrength > 0 && ctx.camera.sssBlurScale > 0;
}
function shouldStoreSSSTransmission(ctx) {
    return ctx.camera.sssStrength > 0 && ctx.camera.sssTransmissionStrength > 0;
}
function getSSSLightingTextureFormat(ctx, attachmentCount, includeSSRSurfaceMRT) {
    const colorFormat = ctx.colorFormat;
    if (!includeSSRSurfaceMRT || attachmentCount === 0) {
        return colorFormat;
    }
    const caps = ctx.device.getDeviceCaps();
    const roughnessFormat = ctx.SSRRoughnessTexture?.format ?? colorFormat;
    const normalFormat = ctx.SSRNormalTexture?.format ?? colorFormat;
    const colorBytes = getTextureFormatBytes(ctx, colorFormat) + getTextureFormatBytes(ctx, roughnessFormat) + getTextureFormatBytes(ctx, normalFormat);
    const fullPrecisionBytes = colorBytes + getTextureFormatBytes(ctx, colorFormat) * attachmentCount;
    if (fullPrecisionBytes <= caps.framebufferCaps.maxColorAttachmentBytesPerSample) {
        return colorFormat;
    }
    return 'rgba8unorm';
}
function getFullMipLevelCount(width, height) {
    return Math.max(1, Math.floor(Math.log2(Math.max(1, width, height))) + 1);
}
function hasSurfaceMRT(ctx) {
    return !!(ctx.materialFlags & SURFACE_MRT_FLAGS);
}
function getLightPassColorAttachments(ctx, colorAttachment) {
    const attachments = [
        colorAttachment
    ];
    if (ctx.materialFlags & MaterialVaryingFlags.SSR_STORE_ROUGHNESS) {
        attachments.push(ctx.SSRRoughnessTexture, ctx.SSRNormalTexture);
    } else if (ctx.materialFlags & MaterialVaryingFlags.SSS_STORE_NORMAL) {
        attachments.push(ctx.SSRNormalTexture);
    }
    if (ctx.materialFlags & MaterialVaryingFlags.SSS_STORE_PROFILE) {
        attachments.push(ctx.SSSProfileTexture, ctx.SSSParamTexture);
    }
    if (ctx.materialFlags & MaterialVaryingFlags.SSS_STORE_DIFFUSE) {
        attachments.push(ctx.SSSDiffuseTexture);
    }
    if (ctx.materialFlags & MaterialVaryingFlags.SSS_STORE_TRANSMISSION) {
        attachments.push(ctx.SSSTransmissionTexture);
    }
    return attachments.length === 1 ? attachments[0] : attachments;
}
/**
 * Derive pipeline options from the current scene/camera state.
 * @internal
 */ function deriveForwardPlusOptions(scene, camera, deviceType, renderQueue) {
    const ssr = camera.SSR && scene.env.light.envLight && scene.env.light.envLight.hasRadiance();
    const sss = camera.SSS && renderQueueHasActiveSSS(renderQueue);
    const needSceneColor = renderQueue.needSceneColor();
    const needSceneColorWithDepth = renderQueue.needSceneColorWithDepth();
    return {
        depthPrepass: true,
        motionVectors: deviceType !== 'webgl' && (camera.TAA || camera.motionBlur || !!ssr && camera.ssrTemporal),
        hiZ: camera.HiZ && deviceType !== 'webgl',
        ssr: !!ssr,
        ssrCalcThickness: !!ssr && camera.ssrCalcThickness,
        gpuPicking: !!camera.getPickResultResolveFunc(),
        needSceneColor,
        needSceneColorWithDepth,
        needsTransmissionDepthForSSR: !!ssr && needSceneColor && !needSceneColorWithDepth,
        sss: !!sss
    };
}
function buildForwardPlusGraphInternal(graph, ctx, renderQueue, options) {
    const backbuffer = graph.importTexture('backbuffer');
    ctx.SSS = !!options.sss;
    // Shared mutable frame state
    const frame = {
        ctx,
        renderQueue,
        depthFramebuffer: null,
        sunLightColor: null,
        options,
        intermediateDeviceStatePushed: false,
        renderQueueDisposed: false,
        clusteredLightReleased: false,
        sunLightRestored: false
    };
    // ── 1. Sky Update ─────────────────────────────────────────────────
    let orderToken = graph.addPass('SkyUpdate', (builder)=>{
        const done = builder.createToken('SkyUpdateDone');
        builder.sideEffect();
        builder.setExecute(()=>{
            frame.sunLightColor = ctx.scene.env.sky.update(ctx);
        });
        return done;
    });
    // ── 2. Clustered Light Setup ──────────────────────────────────────
    orderToken = graph.addPass('ClusterLights', (builder)=>{
        builder.read(orderToken);
        const done = builder.createToken('ClusterLightsDone');
        builder.sideEffect();
        builder.setExecute(()=>{
            ctx.clusteredLight = getClusteredLight();
            ctx.clusteredLight.calculateLightIndex(ctx.camera, renderQueue);
        });
        return done;
    });
    // ── 3. GPU Picking (optional, sideEffect) ─────────────────────────
    if (options.gpuPicking) {
        orderToken = graph.addPass('GPUPicking', (builder)=>{
            builder.read(orderToken);
            const done = builder.createToken('GPUPickingDone');
            builder.sideEffect();
            builder.setExecute(()=>{
                const pickResolveFunc = ctx.camera.getPickResultResolveFunc();
                if (pickResolveFunc) {
                    renderObjectColors(ctx, pickResolveFunc, renderQueue);
                }
            });
            return done;
        });
    }
    // ── 4. Shadow Maps ────────────────────────────────────────────────
    // Shadow maps are managed internally by lights, mark as side effect
    if (renderQueue.shadowedLights.length > 0) {
        orderToken = graph.addPass('ShadowMaps', (builder)=>{
            builder.read(orderToken);
            const done = builder.createToken('ShadowMapsDone');
            builder.sideEffect();
            builder.setExecute(()=>{
                renderShadowMaps(ctx, renderQueue.shadowedLights);
            });
            return done;
        });
    }
    // ── 5. Depth Prepass ──────────────────────────────────────────────
    // Declare transient depth and motion vector textures
    const depthPassResult = graph.addPass('DepthPrepass', (builder)=>{
        builder.read(orderToken);
        const format = ctx.device.type === 'webgl' ? ctx.SSRCalcThickness ? 'rgba16f' : 'rgba8unorm' : ctx.SSRCalcThickness ? 'rg32f' : 'r32f';
        const mvFormat = 'rgba16f';
        const depthHandle = builder.createTexture({
            format,
            label: 'linearDepth'
        });
        const motionVectorHandle = options.motionVectors ? builder.createTexture({
            format: mvFormat,
            label: 'motionVector'
        }) : undefined;
        const finalDepthAttachment = ctx.finalFramebuffer?.getDepthAttachment();
        const externalDepthAttachment = finalDepthAttachment?.isTexture2D() ? finalDepthAttachment : null;
        const graphDepthAttachmentHandle = externalDepthAttachment ? undefined : builder.createTexture({
            format: ctx.depthFormat,
            label: 'sceneDepth'
        });
        const depthAttachmentOrFormat = externalDepthAttachment ?? graphDepthAttachmentHandle ?? ctx.depthFormat;
        const depthFramebufferHandle = builder.createFramebuffer({
            label: 'DepthPrepassFramebuffer',
            width: ctx.renderWidth,
            height: ctx.renderHeight,
            colorAttachments: motionVectorHandle ? [
                depthHandle,
                motionVectorHandle
            ] : depthHandle,
            depthAttachment: depthAttachmentOrFormat,
            ignoreDepthStencil: false
        });
        const skyMotionVectorFramebufferHandle = motionVectorHandle ? builder.createFramebuffer({
            label: 'SkyMotionVectorFramebuffer',
            width: ctx.renderWidth,
            height: ctx.renderHeight,
            colorAttachments: motionVectorHandle,
            depthAttachment: depthAttachmentOrFormat
        }) : undefined;
        builder.addSubpass('SceneDepth', (rgCtx)=>{
            const depthFramebuffer = rgCtx.getFramebuffer(depthFramebufferHandle);
            frame.depthFramebuffer = renderSceneDepth(frame, depthFramebuffer, rgCtx, undefined, undefined, false);
        });
        if (skyMotionVectorFramebufferHandle) {
            builder.addSubpass('SkyMotionVectors', (rgCtx)=>{
                renderSkyMotionVectors(ctx, rgCtx, skyMotionVectorFramebufferHandle);
            });
        }
        return {
            depthHandle,
            motionVectorHandle,
            graphDepthAttachmentHandle,
            externalDepthAttachment,
            depthFramebufferHandle
        };
    });
    const depthHandle = depthPassResult.depthHandle;
    const motionVectorHandle = depthPassResult.motionVectorHandle;
    const renderDepthAttachment = depthPassResult.graphDepthAttachmentHandle ?? depthPassResult.externalDepthAttachment ?? null;
    let preLightTransmissionDepthToken;
    if (options.needsTransmissionDepthForSSR) {
        preLightTransmissionDepthToken = graph.addPass('TransmissionDepthForSSR', (builder)=>{
            builder.read(depthPassResult.depthFramebufferHandle);
            const done = builder.createToken('TransmissionDepthForSSRDone');
            builder.sideEffect();
            builder.setExecute((rgCtx)=>{
                renderTransmissionDepthPass(frame, rgCtx);
            });
            return done;
        });
    }
    // ── 6. Hi-Z (optional) ───────────────────────────────────────────
    let hiZHandle;
    if (options.hiZ) {
        graph.addPass('HiZ', (builder)=>{
            builder.read(depthHandle);
            builder.read(depthPassResult.depthFramebufferHandle);
            if (preLightTransmissionDepthToken) {
                builder.read(preLightTransmissionDepthToken);
            }
            hiZHandle = builder.createTexture({
                format: 'r32f',
                label: 'hiZ',
                mipLevels: getFullMipLevelCount(ctx.renderWidth, ctx.renderHeight)
            });
            const hiZFramebufferHandle = builder.createFramebuffer({
                label: 'HiZFramebuffer',
                colorAttachments: hiZHandle,
                depthAttachment: null
            });
            builder.setExecute((rgCtx)=>{
                const ctx = frame.ctx;
                // Use the depth texture from the framebuffer (which contains the RenderGraph texture)
                const depthTex = frame.depthFramebuffer?.getDepthAttachment();
                if (depthTex) {
                    // Get the HiZ texture allocated by the executor
                    const hiZTex = rgCtx.getTexture(hiZHandle);
                    const HiZFrameBuffer = rgCtx.getFramebuffer(hiZFramebufferHandle);
                    buildHiZ(depthTex, HiZFrameBuffer);
                    ctx.HiZTexture = hiZTex;
                }
            });
        });
    }
    // ── 7. Main Light Pass ────────────────────────────────────────────
    const historyManager = ctx.camera?.getHistoryResourceManager?.() ?? null;
    const lightHistoryReadBindings = [];
    const compositeHistoryReadBindings = [];
    const historySize = {
        width: ctx.renderWidth,
        height: ctx.renderHeight
    };
    if (historyManager && options.ssr && ctx.camera?.ssrTemporal && options.motionVectors) {
        const reflectHistoryHandle = historyManager.importPreviousIfCompatible(graph, RGHistoryResources.SSR_REFLECT, {
            format: 'rgba16f',
            sizeMode: 'absolute',
            width: ctx.renderWidth,
            height: ctx.renderHeight
        }, historySize);
        const motionVectorHistoryHandle = historyManager.importPreviousIfCompatible(graph, RGHistoryResources.SSR_MOTION_VECTOR, {
            format: 'rgba16f',
            sizeMode: 'absolute',
            width: ctx.renderWidth,
            height: ctx.renderHeight
        }, historySize);
        if (reflectHistoryHandle && motionVectorHistoryHandle) {
            lightHistoryReadBindings.push({
                name: RGHistoryResources.SSR_REFLECT,
                handle: reflectHistoryHandle
            }, {
                name: RGHistoryResources.SSR_MOTION_VECTOR,
                handle: motionVectorHistoryHandle
            });
        }
    }
    if (historyManager && ctx.camera?.TAA && options.motionVectors) {
        const colorHistoryHandle = historyManager.importPreviousIfCompatible(graph, RGHistoryResources.TAA_COLOR, {
            format: ctx.colorFormat,
            sizeMode: 'absolute',
            width: ctx.renderWidth,
            height: ctx.renderHeight
        }, historySize);
        const motionVectorHistoryHandle = historyManager.importPreviousIfCompatible(graph, RGHistoryResources.TAA_MOTION_VECTOR, {
            format: 'rgba16f',
            sizeMode: 'absolute',
            width: ctx.renderWidth,
            height: ctx.renderHeight
        }, historySize);
        if (colorHistoryHandle && motionVectorHistoryHandle) {
            compositeHistoryReadBindings.push({
                name: RGHistoryResources.TAA_COLOR,
                handle: colorHistoryHandle
            }, {
                name: RGHistoryResources.TAA_MOTION_VECTOR,
                handle: motionVectorHistoryHandle
            });
        }
    }
    let sssProfileResult;
    if (options.sss) {
        sssProfileResult = graph.addPass('SSSProfile', (builder)=>{
            builder.read(depthHandle);
            builder.read(depthPassResult.depthFramebufferHandle);
            if (preLightTransmissionDepthToken) {
                builder.read(preLightTransmissionDepthToken);
            }
            const profileHandle = builder.createTexture({
                format: 'rgba16f',
                label: 'sssProfile'
            });
            const paramHandle = builder.createTexture({
                format: 'rgba8unorm',
                label: 'sssParam'
            });
            const normalHandle = options.ssr ? undefined : builder.createTexture({
                format: getSurfaceTextureFormat(ctx),
                label: 'sssNormal'
            });
            const colorAttachments = normalHandle ? [
                ctx.colorFormat,
                normalHandle,
                profileHandle,
                paramHandle
            ] : [
                ctx.colorFormat,
                profileHandle,
                paramHandle
            ];
            const framebufferHandle = builder.createFramebuffer({
                label: 'SSSProfileFramebuffer',
                width: ctx.renderWidth,
                height: ctx.renderHeight,
                colorAttachments,
                depthAttachment: renderDepthAttachment,
                ignoreDepthStencil: false
            });
            builder.setExecute((rgCtx)=>{
                renderForwardSSSProfile(frame, rgCtx.getFramebuffer(framebufferHandle), rgCtx.getTexture(profileHandle), rgCtx.getTexture(paramHandle), normalHandle ? rgCtx.getTexture(normalHandle) : null);
            });
            return {
                profileHandle,
                paramHandle,
                normalHandle,
                framebufferHandle
            };
        });
    }
    const lightPassResult = graph.addPass('LightPass', (builder)=>{
        builder.read(depthHandle);
        builder.read(depthPassResult.depthFramebufferHandle);
        if (preLightTransmissionDepthToken) {
            builder.read(preLightTransmissionDepthToken);
        }
        if (hiZHandle) {
            builder.read(hiZHandle);
        }
        for (const binding of lightHistoryReadBindings){
            builder.read(binding.handle);
        }
        // Create scene color texture (intermediate render target)
        const sceneColorHandle = builder.createTexture({
            format: ctx.colorFormat,
            label: 'sceneColor'
        });
        // Create optional sceneColorCopy for transmission/refraction materials
        let sceneColorCopyHandle;
        if (options.needSceneColor) {
            sceneColorCopyHandle = builder.createTexture({
                format: ctx.colorFormat,
                label: 'sceneColorCopy'
            });
        }
        if (sssProfileResult) {
            builder.read(sssProfileResult.profileHandle);
            builder.read(sssProfileResult.paramHandle);
            if (sssProfileResult.normalHandle) {
                builder.read(sssProfileResult.normalHandle);
            }
        }
        const includeSSRSurfaceMRT = !!options.ssr;
        const writeSSSDiffuse = options.sss && shouldStoreSSSDiffuse(ctx);
        let writeSSSTransmission = options.sss && shouldStoreSSSTransmission(ctx);
        if (writeSSSDiffuse && writeSSSTransmission && includeSSRSurfaceMRT && getSSSLightingTextureFormat(ctx, 2, includeSSRSurfaceMRT) !== ctx.colorFormat) {
            writeSSSTransmission = false;
        }
        const sssLightingAttachmentCount = (writeSSSDiffuse ? 1 : 0) + (writeSSSTransmission ? 1 : 0);
        const sssLightingFormat = getSSSLightingTextureFormat(ctx, sssLightingAttachmentCount, includeSSRSurfaceMRT);
        const sssDiffuseHandle = writeSSSDiffuse ? builder.createTexture({
            format: sssLightingFormat,
            label: 'sssDiffuse'
        }) : undefined;
        const sssTransmissionHandle = writeSSSTransmission ? builder.createTexture({
            format: sssLightingFormat,
            label: 'sssTransmission'
        }) : undefined;
        const useFinalFramebufferAsIntermediate = !!depthPassResult.externalDepthAttachment && depthPassResult.externalDepthAttachment === ctx.finalFramebuffer?.getDepthAttachment();
        const sceneColorFramebufferHandle = useFinalFramebufferAsIntermediate ? undefined : builder.createFramebuffer({
            label: 'SceneColorFramebuffer',
            width: ctx.renderWidth,
            height: ctx.renderHeight,
            colorAttachments: sceneColorHandle,
            depthAttachment: renderDepthAttachment
        });
        // SSR may pre-insert transmission depth before LightPass for Hi-Z. In that case the
        // refraction scene-color copy needs an isolated depth buffer so transmission surfaces
        // do not occlude the background color they are about to sample.
        const sceneColorCopyFramebufferHandle = sceneColorCopyHandle && !options.needsTransmissionDepthForSSR ? builder.createFramebuffer({
            label: 'SceneColorCopyFramebuffer',
            width: ctx.renderWidth,
            height: ctx.renderHeight,
            colorAttachments: sceneColorCopyHandle,
            depthAttachment: renderDepthAttachment,
            ignoreDepthStencil: false
        }) : undefined;
        builder.setExecute((rgCtx)=>{
            const sceneColorTex = rgCtx.getTexture(sceneColorHandle);
            const sceneColorCopyTex = sceneColorCopyHandle ? rgCtx.getTexture(sceneColorCopyHandle) : null;
            if (sssProfileResult) {
                ctx.SSSProfileTexture = rgCtx.getTexture(sssProfileResult.profileHandle);
                ctx.SSSParamTexture = rgCtx.getTexture(sssProfileResult.paramHandle);
                if (sssProfileResult.normalHandle) {
                    ctx.SSRNormalTexture = rgCtx.getTexture(sssProfileResult.normalHandle);
                }
            }
            ctx.SSSDiffuseTexture = sssDiffuseHandle ? rgCtx.getTexture(sssDiffuseHandle) : null;
            ctx.SSSTransmissionTexture = sssTransmissionHandle ? rgCtx.getTexture(sssTransmissionHandle) : null;
            const renderLightPass = ()=>renderMainLightPass(frame, sceneColorTex, sceneColorCopyTex, rgCtx, sceneColorFramebufferHandle, sceneColorCopyFramebufferHandle);
            if (historyManager && lightHistoryReadBindings.length > 0) {
                historyManager.beginReadScope(lightHistoryReadBindings.map((binding)=>({
                        name: binding.name,
                        texture: rgCtx.getTexture(binding.handle)
                    })));
                try {
                    renderLightPass();
                } finally{
                    historyManager.endReadScope();
                }
            } else {
                renderLightPass();
            }
        });
        return {
            sceneColorHandle,
            sceneColorCopyHandle,
            sceneColorFramebufferHandle
        };
    });
    const sceneColorHandle = lightPassResult.sceneColorHandle;
    // 8. Transmission depth pass (optional)
    let transmissionDepthToken;
    if (options.needSceneColor && !options.needsTransmissionDepthForSSR) {
        transmissionDepthToken = graph.addPass('TransmissionDepth', (builder)=>{
            builder.read(sceneColorHandle);
            builder.read(depthPassResult.depthFramebufferHandle);
            const done = builder.createToken('TransmissionDepthDone');
            builder.sideEffect();
            builder.setExecute((rgCtx)=>{
                renderTransmissionDepthPass(frame, rgCtx);
            });
            return done;
        });
    }
    // 9. Post effects + final composite
    const presentedBackbuffer = graph.addPass('Composite', (builder)=>{
        builder.read(sceneColorHandle);
        builder.read(depthHandle);
        if (hiZHandle) {
            builder.read(hiZHandle);
        }
        if (motionVectorHandle) {
            builder.read(motionVectorHandle);
        }
        if (lightPassResult.sceneColorFramebufferHandle) {
            builder.read(lightPassResult.sceneColorFramebufferHandle);
        }
        if (transmissionDepthToken) {
            builder.read(transmissionDepthToken);
        }
        for (const binding of compositeHistoryReadBindings){
            builder.read(binding.handle);
        }
        const outputBackbuffer = builder.write(backbuffer);
        builder.setExecute((rgCtx)=>{
            if (historyManager && compositeHistoryReadBindings.length > 0) {
                historyManager.beginReadScope(compositeHistoryReadBindings.map((binding)=>({
                        name: binding.name,
                        texture: rgCtx.getTexture(binding.handle)
                    })));
                try {
                    renderComposite(frame);
                } finally{
                    historyManager.endReadScope();
                }
            } else {
                renderComposite(frame);
            }
        });
        return outputBackbuffer;
    });
    return {
        backbuffer: presentedBackbuffer,
        frame
    };
}
// ─── Pass Implementation Helpers ────────────────────────────────────
// These wrap the existing SceneRenderer static methods, adapted to work
// with the FrameState pattern. They contain the same logic as the
// original methods but read/write through FrameState.
/** @internal */ function renderShadowMaps(ctx, lights) {
    ctx.renderPass = _shadowMapPass;
    ctx.device.pushDeviceStates();
    try {
        for (const light of lights){
            light.shadow.render(ctx, _shadowMapPass);
        }
    } finally{
        ctx.device.popDeviceStates();
    }
}
function renderForwardSSSProfile(frame, profileFramebuffer, profileTexture, paramTexture, normalTexture) {
    const { ctx, renderQueue } = frame;
    if (!ctx.SSS || !ctx.depthTexture) {
        return;
    }
    const sssRenderQueue = createActualSSSRenderQueue(renderQueue);
    if (!sssRenderQueue) {
        return;
    }
    const device = ctx.device;
    const savedMaterialFlags = ctx.materialFlags;
    const savedCompositor = ctx.compositor;
    const savedTransmission = _scenePass.transmission;
    const savedRenderOpaque = _scenePass.renderOpaque;
    const savedRenderTransparent = _scenePass.renderTransparent;
    const savedClearColor = _scenePass.clearColor;
    const savedClearDepth = _scenePass.clearDepth;
    const savedClearStencil = _scenePass.clearStencil;
    const savedCommandBufferReuse = ctx.camera.commandBufferReuse;
    const savedProfileTexture = ctx.SSSProfileTexture;
    const savedParamTexture = ctx.SSSParamTexture;
    const savedNormalTexture = ctx.SSRNormalTexture;
    let profileFlags = MaterialVaryingFlags.SSS_STORE_PROFILE;
    if (normalTexture) {
        profileFlags |= MaterialVaryingFlags.SSS_STORE_NORMAL;
    }
    device.pushDeviceStates();
    try {
        device.setFramebuffer(profileFramebuffer);
        ctx.SSSProfileTexture = profileTexture;
        ctx.SSSParamTexture = paramTexture;
        ctx.SSRNormalTexture = normalTexture;
        ctx.compositor = null;
        ctx.camera.commandBufferReuse = false;
        ctx.materialFlags = ctx.materialFlags & ~(MaterialVaryingFlags.SSR_STORE_ROUGHNESS | MaterialVaryingFlags.SSS_STORE_PROFILE | MaterialVaryingFlags.SSS_STORE_NORMAL | MaterialVaryingFlags.SSS_STORE_DIFFUSE | MaterialVaryingFlags.SSS_STORE_TRANSMISSION) | profileFlags;
        _scenePass.transmission = false;
        _scenePass.renderOpaque = true;
        _scenePass.renderTransparent = false;
        _scenePass.clearColor = Vector4.zero();
        _scenePass.clearDepth = null;
        _scenePass.clearStencil = null;
        _scenePass.render(ctx, null, null, sssRenderQueue);
    } finally{
        _scenePass.clearColor = savedClearColor;
        _scenePass.clearDepth = savedClearDepth;
        _scenePass.clearStencil = savedClearStencil;
        _scenePass.renderTransparent = savedRenderTransparent;
        _scenePass.renderOpaque = savedRenderOpaque;
        _scenePass.transmission = savedTransmission;
        ctx.camera.commandBufferReuse = savedCommandBufferReuse;
        ctx.materialFlags = savedMaterialFlags;
        ctx.compositor = savedCompositor;
        ctx.SSSProfileTexture = savedProfileTexture;
        ctx.SSSParamTexture = savedParamTexture;
        ctx.SSRNormalTexture = savedNormalTexture;
        device.popDeviceStates();
        sssRenderQueue.dispose();
    }
}
function releaseIntermediateFramebuffer(frame) {
    const { ctx } = frame;
    if (frame.intermediateDeviceStatePushed) {
        ctx.device.popDeviceStates();
        frame.intermediateDeviceStatePushed = false;
    }
    ctx.intermediateFramebuffer = null;
}
function releaseDepthFramebuffer(frame) {
    frame.depthFramebuffer = null;
}
function disposeRenderQueue(frame) {
    if (!frame.renderQueueDisposed) {
        frame.renderQueue.dispose();
        frame.renderQueueDisposed = true;
    }
}
function releaseClusteredLight(frame) {
    if (!frame.clusteredLightReleased && frame.ctx.clusteredLight) {
        freeClusteredLight(frame.ctx.clusteredLight);
        frame.ctx.clusteredLight = undefined;
        frame.clusteredLightReleased = true;
    }
}
function restoreSunLight(frame) {
    if (!frame.sunLightRestored && frame.sunLightColor && frame.ctx.sunLight) {
        frame.ctx.sunLight.color = frame.sunLightColor;
        frame.sunLightRestored = true;
    }
}
function cleanupFrame(frame) {
    releaseIntermediateFramebuffer(frame);
    releaseDepthFramebuffer(frame);
    releaseClusteredLight(frame);
    disposeRenderQueue(frame);
    restoreSunLight(frame);
}
/** @internal */ function renderSceneDepth(frame, existingDepthFb, rgCtx, depthTex, motionVectorTex, transmissionOverride) {
    const ctx = frame.ctx;
    const renderQueue = frame.renderQueue;
    const transmission = transmissionOverride ?? !!existingDepthFb;
    let depthFramebuffer = existingDepthFb;
    if (!depthFramebuffer) {
        // Use RenderGraph-allocated textures if provided
        {
            // Allocate through RenderGraph so framebuffer lifetime is owned by the executor.
            const format = ctx.device.type === 'webgl' ? ctx.SSRCalcThickness ? 'rgba16f' : 'rgba8unorm' : ctx.SSRCalcThickness ? 'rg32f' : 'r32f';
            const mvFormat = 'rgba16f';
            if (!ctx.finalFramebuffer) {
                depthFramebuffer = rgCtx.createFramebuffer({
                    width: ctx.renderWidth,
                    height: ctx.renderHeight,
                    colorAttachments: ctx.motionVectors ? [
                        format,
                        mvFormat
                    ] : format,
                    depthAttachment: ctx.depthFormat,
                    ignoreDepthStencil: false
                });
            } else {
                const originDepth = ctx.finalFramebuffer?.getDepthAttachment();
                if (originDepth?.isTexture2D()) {
                    depthFramebuffer = rgCtx.createFramebuffer({
                        width: originDepth.width,
                        height: originDepth.height,
                        colorAttachments: ctx.motionVectors ? [
                            format,
                            mvFormat
                        ] : format,
                        depthAttachment: originDepth,
                        ignoreDepthStencil: false
                    });
                } else {
                    depthFramebuffer = rgCtx.createFramebuffer({
                        width: ctx.renderWidth,
                        height: ctx.renderHeight,
                        colorAttachments: ctx.motionVectors ? [
                            format,
                            mvFormat
                        ] : format,
                        depthAttachment: ctx.depthFormat,
                        ignoreDepthStencil: false
                    });
                }
            }
        }
    }
    if (!transmission) {
        frame.depthFramebuffer = depthFramebuffer;
    }
    ctx.device.pushDeviceStates();
    try {
        ctx.device.setFramebuffer(depthFramebuffer);
        _depthPass.encodeDepth = depthFramebuffer.getColorAttachments()[0].format === 'rgba8unorm';
        _depthPass.clearColor = transmission ? null : _depthPass.encodeDepth ? new Vector4(0, 0, 0, 1) : new Vector4(1, 1, 1, 1);
        _depthPass.clearDepth = transmission ? null : 1;
        _depthPass.clearStencil = null;
        _depthPass.transmission = transmission;
        if (ctx.SSRCalcThickness && !transmission) {
            if (!_backDepthColorState) {
                _backDepthColorState = ctx.device.createColorState().setColorMask(false, true, false, false);
            }
            if (!_frontDepthColorState) {
                _frontDepthColorState = ctx.device.createColorState().setColorMask(true, false, false, false);
            }
            ctx.forceColorState = _backDepthColorState;
            ctx.forceCullMode = 'front';
            _depthPass.renderBackface = true;
            _depthPass.transmission = false;
            _depthPass.render(ctx, null, null, renderQueue);
            _depthPass.clearColor = null;
            _depthPass.renderBackface = false;
            ctx.forceColorState = _frontDepthColorState;
            ctx.forceCullMode = null;
        }
        _depthPass.render(ctx, null, null, renderQueue);
    } finally{
        ctx.forceColorState = null;
        ctx.forceCullMode = null;
        _depthPass.renderBackface = false;
        ctx.device.popDeviceStates();
    }
    if (!transmission) {
        ctx.motionVectorTexture = ctx.motionVectors ? depthFramebuffer.getColorAttachments()[1] : null;
        ctx.linearDepthTexture = depthFramebuffer.getColorAttachments()[0];
        ctx.depthTexture = depthFramebuffer.getDepthAttachment();
        ctx.depthPrepassAttachment = ctx.depthTexture;
    // HiZ is now built in the dedicated HiZ pass
    }
    return depthFramebuffer;
}
// ─── Sky Motion Vector State ────────────────────────────────────────
let _skyMVProgram = null;
let _skyMVBindGroup = null;
let _skyMVBox = null;
/** @internal */ function renderSkyMotionVectors(ctx, rgCtx, framebufferHandle) {
    if (!ctx.motionVectorTexture) {
        return;
    }
    const device = ctx.device;
    const fb = framebufferHandle ? rgCtx.getFramebuffer(framebufferHandle) : rgCtx.createFramebuffer({
        colorAttachments: ctx.motionVectorTexture,
        depthAttachment: ctx.depthTexture
    });
    if (!_skyMVProgram) {
        _skyMVProgram = device.buildRenderProgram({
            vertex (pb) {
                this.$inputs.pos = pb.vec3().attrib('position');
                this.VPMatrix = pb.mat4().uniform(0);
                this.prevVPMatrix = pb.mat4().uniform(0);
                this.cameraPos = pb.vec3().uniform(0);
                this.prevCameraPos = pb.vec3().uniform(0);
                pb.main(function() {
                    this.$l.worldPos = pb.add(this.$inputs.pos, this.cameraPos);
                    this.$l.prevWorldPos = pb.add(this.$inputs.pos, this.prevCameraPos);
                    this.$l.clipPos = pb.mul(this.VPMatrix, pb.vec4(this.worldPos, 1));
                    this.$l.prevClipPos = pb.mul(this.prevVPMatrix, pb.vec4(this.prevWorldPos, 1));
                    this.clipPos.z = this.clipPos.w;
                    this.$builtins.position = this.clipPos;
                    this.$outputs.currentPos = this.clipPos;
                    this.$outputs.prevPos = this.prevClipPos;
                });
            },
            fragment (pb) {
                this.$outputs.color = pb.vec4();
                pb.main(function() {
                    this.$l.motionVector = pb.mul(pb.sub(pb.div(this.$inputs.currentPos.xy, this.$inputs.currentPos.w), pb.div(this.$inputs.prevPos.xy, this.$inputs.prevPos.w)), 0.5);
                    this.$outputs.color = pb.vec4(this.motionVector, 0, 1);
                });
            }
        });
        _skyMVProgram.name = '@TAA_SkyMotionVector';
    }
    if (!_skyMVBindGroup) {
        _skyMVBindGroup = device.createBindGroup(_skyMVProgram.bindGroupLayouts[0]);
    }
    if (!_skyMVBox) {
        _skyMVBox = new BoxShape({
            size: 2,
            needNormal: false,
            needUV: false
        });
    }
    _skyMVBindGroup.setValue('VPMatrix', ctx.camera.viewProjectionMatrix);
    _skyMVBindGroup.setValue('prevVPMatrix', ctx.camera.prevVPMatrix);
    _skyMVBindGroup.setValue('cameraPos', ctx.camera.getWorldPosition());
    _skyMVBindGroup.setValue('prevCameraPos', ctx.camera.prevPosition);
    device.pushDeviceStates();
    device.setProgram(_skyMVProgram);
    device.setBindGroup(0, _skyMVBindGroup);
    device.setRenderStates(AbstractPostEffect.getDefaultRenderState(ctx, 'le'));
    device.setFramebuffer(fb);
    _skyMVBox.draw();
    device.popDeviceStates();
}
function blitToCurrentColorAttachment(ctx, source) {
    const framebuffer = ctx.device.getFramebuffer();
    const destination = framebuffer?.getColorAttachment(0) ?? null;
    new CopyBlitter().blit(source, destination, fetchSampler('clamp_nearest_nomip'));
}
/** @internal */ function renderMainLightPass(frame, sceneColorTex, sceneColorCopyTex, rgCtx, sceneColorFramebufferHandle, sceneColorCopyFramebufferHandle) {
    const { ctx, renderQueue } = frame;
    const device = ctx.device;
    // Use RenderGraph-allocated scene color texture
    const depthTex = frame.depthFramebuffer?.getDepthAttachment();
    ctx.materialFlags &= ~(MaterialVaryingFlags.SSR_STORE_ROUGHNESS | MaterialVaryingFlags.SSS_STORE_PROFILE | MaterialVaryingFlags.SSS_STORE_DIFFUSE | MaterialVaryingFlags.SSS_STORE_NORMAL | MaterialVaryingFlags.SSS_STORE_TRANSMISSION);
    if (ctx.SSR) {
        ctx.materialFlags |= MaterialVaryingFlags.SSR_STORE_ROUGHNESS;
    }
    if (ctx.SSS) {
        if (ctx.SSSDiffuseTexture) {
            ctx.materialFlags |= MaterialVaryingFlags.SSS_STORE_DIFFUSE;
        }
        if (ctx.SSSTransmissionTexture) {
            ctx.materialFlags |= MaterialVaryingFlags.SSS_STORE_TRANSMISSION;
        }
    }
    if (depthTex === ctx.finalFramebuffer?.getDepthAttachment()) {
        ctx.intermediateFramebuffer = ctx.finalFramebuffer;
    } else if (sceneColorFramebufferHandle && !hasSurfaceMRT(ctx)) {
        ctx.intermediateFramebuffer = rgCtx.getFramebuffer(sceneColorFramebufferHandle);
    } else {
        ctx.intermediateFramebuffer = rgCtx.createFramebuffer({
            width: sceneColorTex.width,
            height: sceneColorTex.height,
            colorAttachments: getLightPassColorAttachments(ctx, sceneColorTex),
            depthAttachment: depthTex
        });
    }
    if (ctx.intermediateFramebuffer && ctx.intermediateFramebuffer !== ctx.finalFramebuffer) {
        device.pushDeviceStates();
        frame.intermediateDeviceStatePushed = true;
        device.setFramebuffer(ctx.intermediateFramebuffer);
    } else {
        device.setViewport(null);
        device.setScissor(null);
    }
    _scenePass.transmission = false;
    _scenePass.clearDepth = depthTex ? null : 1;
    _scenePass.clearStencil = depthTex ? null : 0;
    ctx.compositor?.begin(ctx);
    if (renderQueue.needSceneColor() && sceneColorCopyTex) {
        const compositor = ctx.compositor;
        ctx.compositor = null;
        const isolateSceneColorDepth = frame.options.needsTransmissionDepthForSSR;
        const savedDepthPrepassAttachment = ctx.depthPrepassAttachment;
        const savedClearDepth = _scenePass.clearDepth;
        const savedClearStencil = _scenePass.clearStencil;
        const savedMaterialFlags = ctx.materialFlags;
        // Use RenderGraph-allocated sceneColorCopy texture
        const sceneColorMaterialFlags = ctx.materialFlags & ~SURFACE_MRT_FLAGS;
        const sceneColorFramebuffer = sceneColorCopyFramebufferHandle ? rgCtx.getFramebuffer(sceneColorCopyFramebufferHandle) : rgCtx.createFramebuffer({
            width: sceneColorCopyTex.width,
            height: sceneColorCopyTex.height,
            colorAttachments: sceneColorCopyTex,
            depthAttachment: isolateSceneColorDepth ? ctx.depthFormat : depthTex,
            ignoreDepthStencil: false
        });
        let sceneColorStatePushed = false;
        try {
            device.pushDeviceStates();
            sceneColorStatePushed = true;
            device.setFramebuffer(sceneColorFramebuffer);
            _scenePass.transmission = false;
            if (isolateSceneColorDepth) {
                ctx.depthPrepassAttachment = undefined;
                _scenePass.clearDepth = 1;
                _scenePass.clearStencil = 0;
            }
            ctx.materialFlags = sceneColorMaterialFlags;
            _scenePass.render(ctx, null, null, renderQueue);
        } finally{
            ctx.materialFlags = savedMaterialFlags;
            if (isolateSceneColorDepth) {
                ctx.depthPrepassAttachment = savedDepthPrepassAttachment;
                _scenePass.clearDepth = savedClearDepth;
                _scenePass.clearStencil = savedClearStencil;
            }
            if (sceneColorStatePushed) {
                device.popDeviceStates();
            }
            ctx.compositor = compositor;
        }
        ctx.sceneColorTexture = sceneColorCopyTex;
        blitToCurrentColorAttachment(ctx, ctx.sceneColorTexture);
        _scenePass.transmission = true;
        _scenePass.clearColor = null;
        _scenePass.clearDepth = null;
        _scenePass.clearStencil = null;
    }
    _scenePass.render(ctx, null, null, renderQueue);
}
/** @internal */ function renderTransmissionDepthPass(frame, rgCtx) {
    renderSceneDepth(frame, frame.depthFramebuffer, rgCtx);
}
/** @internal */ function renderComposite(frame) {
    const { ctx } = frame;
    ctx.compositor?.drawPostEffects(ctx, PostEffectLayer.end, ctx.linearDepthTexture);
    ctx.compositor?.end(ctx);
    disposeRenderQueue(frame);
    ctx.materialFlags &= ~MaterialVaryingFlags.SSR_STORE_ROUGHNESS;
    ctx.materialFlags &= ~MaterialVaryingFlags.SSS_STORE_PROFILE;
    ctx.materialFlags &= ~MaterialVaryingFlags.SSS_STORE_DIFFUSE;
    ctx.materialFlags &= ~MaterialVaryingFlags.SSS_STORE_NORMAL;
    ctx.materialFlags &= ~MaterialVaryingFlags.SSS_STORE_TRANSMISSION;
    if (ctx.intermediateFramebuffer && ctx.intermediateFramebuffer !== ctx.finalFramebuffer) {
        const blitter = new CopyBlitter();
        blitter.srgbOut = !ctx.finalFramebuffer;
        const srcTex = ctx.intermediateFramebuffer.getColorAttachments()[0];
        blitter.blit(srcTex, ctx.finalFramebuffer ?? null, fetchSampler('clamp_nearest_nomip'));
    }
    releaseIntermediateFramebuffer(frame);
    releaseDepthFramebuffer(frame);
    releaseClusteredLight(frame);
    restoreSunLight(frame);
}
// ─── Convenience: Execute Full Pipeline ─────────────────────────────
/**
 * Build, compile, and execute the forward+ pipeline as a render graph.
 *
 * This is the drop-in replacement for `SceneRenderer._renderScene`.
 *
 * @param ctx - The draw context for this frame.
 * @public
 */ function executeForwardPlusGraph(ctx) {
    const device = ctx.device;
    const graph = new RenderGraph();
    let renderQueue = null;
    let frame = null;
    let executor = null;
    let historyManager = null;
    let historyFrameStarted = false;
    try {
        // Cull scene first (needed to derive options)
        renderQueue = _scenePass.cullScene(ctx, ctx.camera);
        const options = deriveForwardPlusOptions(ctx.scene, ctx.camera, device.type, renderQueue);
        ctx.SSS = options.sss;
        // Ensure the camera has a history resource manager for temporal effects (TAA, motion blur)
        historyManager = ctx.camera.getHistoryResourceManager();
        if (!historyManager) {
            historyManager = new HistoryResourceManager(_devicePoolAllocator);
            ctx.camera.setHistoryResourceManager(historyManager);
        }
        historyManager.beginFrame();
        historyFrameStarted = true;
        const buildResult = buildForwardPlusGraphInternal(graph, ctx, renderQueue, options);
        frame = buildResult.frame;
        const compiled = graph.compile([
            buildResult.backbuffer
        ]);
        // Use RenderGraphExecutor for automatic resource management
        executor = new RenderGraphExecutor(_devicePoolAllocator, ctx.renderWidth, ctx.renderHeight);
        // Register imported backbuffer (if using finalFramebuffer)
        if (ctx.finalFramebuffer) {
            const backbufferTex = ctx.finalFramebuffer.getColorAttachments()[0];
            executor.setImportedTexture(buildResult.backbuffer, backbufferTex);
        }
        historyManager.bindImportedTextures(executor);
        executor.execute(compiled);
        historyManager.commitFrame();
    } finally{
        if (historyFrameStarted) {
            historyManager?.discardFrame();
        }
        if (frame) {
            cleanupFrame(frame);
        } else {
            renderQueue?.dispose();
        }
        executor?.reset();
    }
}

export { deriveForwardPlusOptions, executeForwardPlusGraph };
//# sourceMappingURL=forward_plus_builder.js.map
